// The day report with its D26 stamp (slice 51a, ADR 0046). The stamp comes
// first on purpose: an owner must see whether a number can be relied on
// before reading it.

import { useState, type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../../api/client.js';
import type { DayReport, SalesFigures } from '../../api/pos-types.js';
import { QueryState, Chip, type TabProps } from './common.js';
import { PeriodPanel } from './PeriodPanel.js';
import { formatDateTime, formatRupees, todayInIndia } from './format.js';
import { BLOCKER_TEXT, SETTLEMENT_STATE_TEXT, STATUS_TEXT, TENDER_TEXT, textOr } from './labels.js';

const STATUS_TONE = {
  provisional: 'warn',
  reconciled: 'ok',
  final: 'ok',
  corrected: 'warn',
} as const;

export interface SalesTabProps extends TabProps {
  /** pos:period_close */
  readonly canClose: boolean;
  /** pos:period_reopen */
  readonly canReopen: boolean;
}

export function SalesTab({ orgId, outletId, canClose, canReopen }: SalesTabProps): JSX.Element {
  const [date, setDate] = useState(todayInIndia());
  const report = useQuery({
    queryKey: ['pos-day', orgId, outletId, date],
    queryFn: () =>
      apiFetch<DayReport>(
        `/organizations/${orgId}/outlets/${outletId}/pos/reports/day?businessDate=${date}`,
      ),
  });
  const r = report.data;

  return (
    <section aria-labelledby="sales-heading">
      <h2 id="sales-heading">Sales for a day</h2>
      <label className="inline-field">
        Business date{' '}
        <input
          type="date"
          value={date}
          max={todayInIndia()}
          onChange={(e) => e.target.value && setDate(e.target.value)}
        />
      </label>
      <QueryState loading={report.isLoading} error={report.error} />
      {r ? (
        <>
          <div className={`stamp stamp-${r.stamp.reconciliationStatus}`} data-testid="report-stamp">
            <div>
              <Chip tone={STATUS_TONE[r.stamp.reconciliationStatus]}>
                {STATUS_TEXT[r.stamp.reconciliationStatus].label}
              </Chip>{' '}
              <span>{STATUS_TEXT[r.stamp.reconciliationStatus].detail}</span>
            </div>
            {r.stamp.blockers.length > 0 ? (
              <ul className="stamp-blockers">
                {r.stamp.blockers.map((b) => (
                  <li key={b.code}>{BLOCKER_TEXT[b.code](b.count)}</li>
                ))}
              </ul>
            ) : null}
            <p className="muted stamp-meta">
              Data as of {formatDateTime(r.stamp.dataAsOf)} · generated{' '}
              {formatDateTime(r.stamp.generatedAt)} · grouped by business date ·{' '}
              {r.stamp.lateEventCount} late {r.stamp.lateEventCount === 1 ? 'record' : 'records'} ·
              version {r.stamp.projectionVersion}
            </p>
          </div>

          <PeriodPanel
            orgId={orgId}
            outletId={outletId}
            date={date}
            report={r}
            canClose={canClose}
            canReopen={canReopen}
          />

          <div className="tiles">
            <Tile label="Sales" value={formatRupees(r.sales.original.totalPaise)} />
            <Tile
              label="Bills"
              value={String(r.sales.original.billCount)}
              note={
                r.sales.original.voidedCount > 0
                  ? `${r.sales.original.voidedCount} voided, not counted`
                  : undefined
              }
            />
            <Tile label="Tax in sales" value={formatRupees(r.sales.original.taxPaise)} />
            <Tile
              label="After credit notes"
              value={formatRupees(r.sales.netOriginalPaise)}
              note={
                r.sales.creditNotes.original.count > 0
                  ? `${r.sales.creditNotes.original.count} credit ${r.sales.creditNotes.original.count === 1 ? 'note' : 'notes'}`
                  : undefined
              }
            />
            <Tile
              label="Flagged bills"
              value={String(r.sales.original.flaggedCount)}
              note={r.sales.original.flaggedCount > 0 ? 'see the Bills tab' : undefined}
            />
            <Tile
              label="Tips"
              value={formatRupees(r.sales.original.tipPaise)}
              note="not in sales"
            />
          </div>
          <p className="muted">{r.salesDefinition}</p>

          {r.sales.corrections.billCount > 0 || r.sales.creditNotes.corrections.count > 0 ? (
            <section className="card" aria-label="Corrections">
              <h3>Posted since the day closed</h3>
              <p className="muted">
                These belong to this date but arrived after the day was closed, so they are shown
                here and not added to the figures above.
              </p>
              <Corrections
                sales={r.sales.corrections}
                creditNotes={r.sales.creditNotes.corrections}
              />
            </section>
          ) : null}

          <h3>Collected by tender</h3>
          {r.tenders.length === 0 ? (
            <p className="muted">No payments recorded for this day.</p>
          ) : (
            <table className="pos-table">
              <thead>
                <tr>
                  <th scope="col">Tender</th>
                  <th scope="col">Payments</th>
                  <th scope="col">Amount</th>
                </tr>
              </thead>
              <tbody>
                {r.tenders.map((t) => (
                  <tr key={t.tender}>
                    <th scope="row">{textOr(TENDER_TEXT, t.tender)}</th>
                    <td>{t.count}</td>
                    <td>{formatRupees(t.amountPaise)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {Object.keys(r.settlementStates).length > 0 ? (
            <>
              <h3>Bills by settlement</h3>
              <div className="chip-row">
                {Object.entries(r.settlementStates).map(([state, n]) => (
                  <span key={state} className="chip">
                    {textOr(SETTLEMENT_STATE_TEXT, state)}: {n}
                  </span>
                ))}
              </div>
            </>
          ) : null}
        </>
      ) : null}
    </section>
  );
}

function Tile({
  label,
  value,
  note,
}: {
  readonly label: string;
  readonly value: string;
  readonly note?: string | undefined;
}): JSX.Element {
  return (
    <div className="tile">
      <div className="tile-label">{label}</div>
      <div className="tile-value">{value}</div>
      {note ? <div className="muted tile-note">{note}</div> : null}
    </div>
  );
}

function Corrections({
  sales,
  creditNotes,
}: {
  readonly sales: SalesFigures;
  readonly creditNotes: { readonly count: number; readonly totalPaise: number };
}): JSX.Element {
  return (
    <ul>
      {sales.billCount > 0 ? (
        <li>
          {sales.billCount} {sales.billCount === 1 ? 'bill' : 'bills'} totalling{' '}
          {formatRupees(sales.totalPaise)}
        </li>
      ) : null}
      {creditNotes.count > 0 ? (
        <li>
          {creditNotes.count} credit {creditNotes.count === 1 ? 'note' : 'notes'} totalling{' '}
          {formatRupees(creditNotes.totalPaise)}
        </li>
      ) : null}
    </ul>
  );
}
